import { computed, inject, Signal } from '@angular/core';

import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { Geometry, LayerWithAttributes } from '@connect-services/client/sdk/api';
import { AssertService, GeometryChildrenTypes } from '@connect-services/client/services/assert.service';
import { AttributeInformationContextService } from '@connect-services/client/services/attribute-information-context.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import {
    SelectedEntitiesByLayerId,
    SelectedEntity,
    SelectionStateService,
} from '@connect-services/client/services/map/selection/selection-state.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';

export abstract class AbstractEntityTableView {
    protected readonly activeLayerId = computed<string | undefined>(() =>
        this.attributeInformationContext.activeLayerId(),
    );
    protected readonly selectedEntityIdsByActiveTab = computed<Set<string>>(() => {
        if (!this.isSelectableCheckbox()) {
            return new Set<string>();
        }

        const activeLayers: LayerWithAttributes[] = this.projectEntityTableService.activeLayers();
        const selectedByLayerId: Record<string, SelectedEntity[]> =
            this.selectionStateService.selectedEntitiesByLayerId();

        return activeLayers.reduce((acc: Set<string>, layer: LayerWithAttributes) => {
            const layerSelected: SelectedEntity[] = selectedByLayerId[layer.id] ?? [];

            layerSelected.forEach((entity: SelectedEntity) => {
                if (entity.isInCreationState !== this.isInCreationState) {
                    return;
                }
                acc.add(entity.uuid);
            });

            return acc;
        }, new Set<string>());
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly attributeInformationContext = inject(AttributeInformationContextService);
    protected readonly mapLayerService = inject(MapLayerService);
    protected readonly projectEntityTableService = inject(ProjectEntityTableService);
    protected readonly selectionService = inject(SelectionService);
    protected readonly selectionStateService = inject(SelectionStateService);

    public abstract readonly onlySelected: Signal<boolean>;

    protected abstract readonly isInCreationState: boolean;
    protected abstract readonly isSelectableCheckbox: Signal<boolean>;

    protected getHeaderCheckboxState(activeLayerId: string, entitiesCount: number): CheckboxState {
        const selectedCount: number = this.setSelectedForLayer(activeLayerId).size;

        switch (true) {
            case selectedCount === 0:
                return CheckboxState.UNCHECKED;
            case selectedCount >= entitiesCount:
                return CheckboxState.CHECKED;
            default:
                return CheckboxState.INDETERMINATE;
        }
    }

    protected getSelectedRowCheckboxState(entityId: string): CheckboxState {
        if (this.selectedEntityIdsByActiveTab().has(entityId)) {
            return CheckboxState.CHECKED;
        }

        return CheckboxState.UNCHECKED;
    }

    protected resolveStyleType(geom?: GeometryChildrenTypes): LayerStyleTypeEnum | undefined {
        AssertService.assertGeometryType(geom);

        return this.selectionService.mapSdkGeometryToStyleType(geom);
    }

    protected setSelectedForLayer(activeLayerId: string): Set<string> {
        const selectedByLayerId: Record<string, SelectedEntity[]> =
            this.selectionStateService.selectedEntitiesByLayerId();

        return new Set<string>(
            (selectedByLayerId[activeLayerId] ?? [])
                .filter((entity: SelectedEntity) => entity.isInCreationState === this.isInCreationState)
                .map((entity: SelectedEntity): string => entity.uuid),
        );
    }

    protected toggleEntitySelection(dataEntity: { geom?: Geometry; uuid: string }, event: Event): void {
        event.stopPropagation();
        if (!this.projectEntityTableService.activeLayers()?.length) {
            return;
        }

        const layerId: string = this.projectEntityTableService.activeLayers()[0].id;
        const uuid = dataEntity.uuid;
        let styleType: LayerStyleTypeEnum | undefined;
        try {
            AssertService.assertGeometryType(dataEntity.geom);
            styleType = this.resolveStyleType(dataEntity.geom);
        } catch {
            styleType = undefined;
        }

        this.updateSelectedEntities(layerId, uuid, styleType);

        if (!this.onlySelected()) {
            return;
        }

        this.updateUnSelectedEntities(layerId, uuid, styleType);
    }

    protected updateSelectedEntities(layerId: string, uuid: string, styleType?: LayerStyleTypeEnum): void {
        this.selectionStateService.updateSelectedEntitiesByLayerId(
            (selectedEntitiesByLayerId: SelectedEntitiesByLayerId) => {
                const previousForLayer: SelectedEntity[] = selectedEntitiesByLayerId[layerId] ?? [];
                const previousMap = new Map<string, SelectedEntity>();

                previousForLayer.forEach((selectedEntity: SelectedEntity) => {
                    previousMap.set(selectedEntity.uuid, selectedEntity);
                });

                if (previousMap.has(uuid)) {
                    previousMap.delete(uuid);
                } else {
                    previousMap.set(uuid, {
                        isInCreationState: this.isInCreationState,
                        isVisible: !!styleType && this.mapLayerService.isLayerCurrentlyVisible(layerId),
                        styleType,
                        uuid,
                    });
                }

                const nextForLayer: SelectedEntity[] = Array.from(previousMap.values());

                if (!nextForLayer.length) {
                    return Object.fromEntries(
                        Object.entries(selectedEntitiesByLayerId).filter(
                            ([key]: [string, SelectedEntity[]]) => key !== layerId,
                        ),
                    );
                }

                return {
                    ...selectedEntitiesByLayerId,
                    [layerId]: nextForLayer,
                };
            },
        );
    }

    protected updateUnSelectedEntities(layerId: string, uuid: string, styleType?: LayerStyleTypeEnum): void {
        let unSelection: SelectedEntity[] =
            this.projectEntityTableService.unSelectedEntitiesAfterFilterByLayerId()[layerId] ?? [];

        if (
            !(this.selectionStateService.selectedEntitiesByLayerId()[layerId] ?? []).some(
                (selectedEntity: SelectedEntity): boolean => selectedEntity.uuid === uuid,
            )
        ) {
            unSelection.push({
                isInCreationState: this.isInCreationState,
                isVisible: !!styleType && this.mapLayerService.isLayerCurrentlyVisible(layerId),
                styleType,
                uuid,
            });
        } else {
            unSelection = unSelection.filter((entity: SelectedEntity) => entity.uuid !== uuid);
        }

        this.projectEntityTableService.unSelectedEntitiesAfterFilterByLayerId.update(
            (unSelectedEntitiesByLayerId: SelectedEntitiesByLayerId): SelectedEntitiesByLayerId => {
                unSelectedEntitiesByLayerId[layerId] = [...unSelection];

                return unSelectedEntitiesByLayerId;
            },
        );
    }

    protected abstract toggleAllSelected(): void;
}
